import React, { useState, useEffect } from "react";
import { Star, Quote, User, MessageSquareQuote } from "lucide-react";
import testimonialsService from "./services/testimonialsService";

export default function TestimonialsSection() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadTestimonials() {
      try {
        setLoading(true);
        const data = await testimonialsService.getPublished();
        setTestimonials(data || []);
      } catch (err) {
        console.error("Error loading public testimonials:", err);
      } finally {
        setLoading(false);
      }
    }
    loadTestimonials();
  }, []);

  return (
    <section
      id="testimonials"
      className="relative py-24 bg-gradient-to-b from-white via-slate-50/50 to-white overflow-hidden"
    >
      {/* Aurora Ambient Glows */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-[#9BB7A3]/10 rounded-full blur-[120px]"></div>
        <div className="absolute -bottom-20 -right-40 w-96 h-96 bg-[#9BB7A3]/10 rounded-full blur-[120px]"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#9BB7A3] font-bold tracking-[4px] uppercase text-xs sm:text-sm block">
            Testimonials
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-800 mt-3">
            What Our Clients Say
          </h2>
          <div className="w-20 h-1 bg-[#9BB7A3] mx-auto mt-4 rounded-full"></div>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto text-sm sm:text-base">
            Real feedback and reviews from homeowners and business leaders who trusted VJ Dsign Studio.
          </p>
        </div>

        {/* Testimonial Cards Grid */}
        {loading ? (
          <div className="py-20 flex justify-center items-center">
            <div className="w-10 h-10 border-3 border-[#9BB7A3]/30 border-t-[#9BB7A3] rounded-full animate-spin"></div>
          </div>
        ) : testimonials.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-3xl border border-slate-100 p-8 max-w-md mx-auto shadow-sm">
            <div className="w-16 h-16 rounded-2xl bg-[#9BB7A3]/10 text-[#9BB7A3] flex items-center justify-center mx-auto mb-4">
              <MessageSquareQuote size={32} />
            </div>
            <h3 className="text-lg font-bold text-slate-800">No Reviews Yet</h3>
            <p className="text-xs text-slate-500 mt-1">
              Client reviews will appear here once published in the admin panel.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((item) => (
              <div
                key={item.id}
                className="
                  bg-white/90
                  backdrop-blur-md
                  rounded-[32px]
                  p-8
                  border border-slate-100
                  shadow-lg
                  hover:shadow-2xl
                  hover:-translate-y-2
                  transition-all
                  duration-500
                  flex flex-col justify-between
                  relative
                  group
                "
              >
                {/* Decorative Quote Icon Background */}
                <div className="absolute top-6 right-6 text-[#9BB7A3]/15 group-hover:text-[#9BB7A3]/25 transition-colors pointer-events-none">
                  <Quote size={48} />
                </div>

                <div>
                  {/* Rating Stars */}
                  <div className="flex items-center gap-1 text-amber-400 mb-5">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={16}
                        fill={i < (item.rating || 5) ? "currentColor" : "none"}
                        className={i < (item.rating || 5) ? "text-amber-400" : "text-slate-200"}
                      />
                    ))}
                  </div>

                  {/* Review Text */}
                  <p className="text-slate-700 text-sm sm:text-base italic leading-relaxed relative z-10 mb-8">
                    "{item.review}"
                  </p>
                </div>

                {/* Client Profile Footer */}
                <div className="flex items-center gap-4 pt-4 border-t border-slate-100">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-100 border border-[#9BB7A3]/30 flex items-center justify-center shrink-0">
                    {item.client_image_url ? (
                      <img
                        src={item.client_image_url}
                        alt={item.client_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User size={20} className="text-[#9BB7A3]" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-bold text-slate-900 text-sm truncate">
                      {item.client_name}
                    </h4>
                    <p className="text-xs font-semibold text-[#9BB7A3] truncate mt-0.5">
                      {item.company || "Verified Client"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
